import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { AnimatedNumber } from '../../os3/theme';
import { TEAM_AVATARS } from '../../components/teamAvatars';
import { AgentHelp } from '../../components/AgentHelp';
import { isRestDemo, overlayRestDemo } from '../../demo/restDemo';

/* Restaurant AI OS — shared primitives. Dark enterprise theme. Kitxens = invisible infrastructure. */

const API = process.env.REACT_APP_BACKEND_URL;

export async function restFetch(path, options = {}) {
  const r = await fetch(`${API}/api/rest${path}`, { credentials: 'include', ...options });
  const method = (options.method || 'GET').toUpperCase();
  if (method === 'GET' && r.ok && isRestDemo()) {
    try {
      const j = await r.clone().json();
      const out = overlayRestDemo(path, j);
      if (out !== j) return new Response(JSON.stringify(out), { status: 200, headers: { 'Content-Type': 'application/json' } });
    } catch { /* keep original response */ }
  }
  return r;
}

export function useRest(path) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  // Mid-demo a 520 must NEVER surface: retry with backoff, keep stale data, self-heal.
  const load = async () => {
    setLoading(true);
    let lastErr = null;
    for (let i = 0; i < 3; i += 1) {
      try {
        const r = await restFetch(path);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        setData(j); setErr(null); setLoading(false);
        return;
      } catch (e) {
        lastErr = e;
        if (i < 2) await new Promise((res) => setTimeout(res, 700 * (i + 1)));
      }
    }
    setErr(lastErr ? lastErr.message : 'error');
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [path]);
  useEffect(() => {
    if (!err) return undefined;
    const t = setTimeout(load, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [err]);
  return { data, err, loading, reload: load };
}

export function RestReconnect({ isEs: isEsProp }) {
  let isEs = isEsProp;
  if (isEs === undefined) { try { isEs = (localStorage.getItem('kx_lang') || '').startsWith('es'); } catch { isEs = false; } }
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3.5 text-amber-800 max-w-md" data-testid="rest-reconnect">
      <span className="w-4 h-4 rounded-full border-2 border-amber-500 border-t-transparent animate-spin shrink-0" />
      <div>
        <p className="text-[13px] font-bold">{isEs ? 'Reconectando con tu instancia…' : 'Reconnecting to your instance…'}</p>
        <p className="text-[11.5px] opacity-80">{isEs ? 'Un segundo — recuperamos tus datos automáticamente.' : 'One moment — your data comes back automatically.'}</p>
      </div>
    </div>
  );
}

export const money = (v) => `$${Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
export const money2 = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function RCard({ children, className = '', testid, ...rest }) {
  return (
    <div data-testid={testid} {...rest} className={`rounded-2xl bg-white border border-gray-200 ${className}`}>
      {children}
    </div>
  );
}

const SOURCE_LABEL = {
  manual_imports: ['Imported Report', 'Reporte Importado'],
  connected_api: ['Connected API', 'API Conectada'],
  kitxens_db: ['Kitxens Database', 'Base de datos Kitxens'],
  manual_entry: ['Manual Entry', 'Entrada Manual'],
  not_connected: ['Not Connected', 'No Conectado'],
  ai: ['AI Generated', 'Generado por IA'],
};

export function SourceBadge({ source, isEs, className = '' }) {
  const label = (SOURCE_LABEL[source] || [source, source])[isEs ? 1 : 0];
  const off = source === 'not_connected';
  return (
    <span data-testid="rest-source-badge" className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${off ? 'border-gray-200 text-gray-400' : 'border-emerald-300 text-emerald-600 bg-emerald-50'} ${className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${off ? 'bg-neutral-600' : 'bg-emerald-500'}`} />
      {isEs ? 'Fuente' : 'Source'}: {label}
    </span>
  );
}

export function RStat({ label, value, sub, delta, testid, tip }) {
  const up = typeof delta === 'number' && delta >= 0;
  return (
    <div data-testid={testid} className="group relative rounded-2xl bg-white border border-gray-200 p-5 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgba(15,18,25,0.08)]">
      <span className="absolute inset-x-0 top-0 h-[3px] rounded-t-2xl" style={{ background: 'linear-gradient(90deg,var(--rest-accent,#F77F11),transparent)' }} aria-hidden="true" />
      <p className="text-[10.5px] uppercase tracking-[0.15em] text-gray-400 font-bold">{label}</p>
      <p className={`mt-2 font-bold tracking-tight tabular-nums text-gray-900 ${typeof value === 'number' || /^[\$€]?[\d,.]+/.test(String(value ?? '')) ? 'text-[26px] whitespace-nowrap' : 'text-[19px] sm:text-[24px] leading-snug break-words'}`}>
        {typeof value === 'number' ? <AnimatedNumber value={value} /> : <CountUp text={value} />}
      </p>
      {typeof delta === 'number' && (
        <p className={`mt-1 text-[12px] font-semibold tabular-nums ${up ? 'text-emerald-600' : 'text-red-600'}`}>
          {up ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
        </p>
      )}
      {sub && <p className="mt-1 text-[11.5px] text-gray-400">{sub}</p>}
      {tip && (
        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-30 w-56 rounded-xl bg-[#141518] border border-white/10 p-3 text-left opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-2xl hidden sm:block"
          data-testid={`${testid}-tip`}>
          <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-[#F77F11]">{tip.title}</p>
          {tip.rows.map(([tk, tv]) => (
            <p key={tk} className="mt-1 flex justify-between gap-3 text-[11px] text-white/75">
              <span>{tk}</span><b className="tabular-nums text-white">{tv}</b>
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export function RestLoading() {
  return (
    <div className="space-y-5" data-testid="rest-loading" aria-busy="true">
      <div className="space-y-2">
        <div className="kx-skel h-7 w-56 max-w-full" />
        <div className="kx-skel h-3.5 w-80 max-w-full" />
      </div>
      <div className="rounded-2xl border border-gray-100 bg-white p-4 flex items-center gap-3">
        <div className="kx-skel w-10 h-10 rounded-full shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="kx-skel h-3 w-24" />
          <div className="kx-skel h-3.5 w-3/4" />
        </div>
        <div className="kx-skel h-9 w-24 shrink-0 hidden sm:block" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-2xl border border-gray-100 bg-white p-4">
            <div className="kx-skel h-3 w-20" />
            <div className="kx-skel h-8 w-28 mt-3" />
            <div className="kx-skel h-3 w-24 mt-2.5" />
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-gray-100 bg-white p-5">
        <div className="kx-skel h-3.5 w-36" />
        <div className="mt-5 flex items-end gap-2.5 h-40">
          {[45, 72, 58, 88, 64, 80].map((h, i) => (
            <div key={i} className="kx-skel flex-1" style={{ height: `${h}%`, borderRadius: '8px 8px 4px 4px' }} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function CountUp({ text, dur = 900 }) {
  const [t, setT] = useState(String(text ?? ''));
  useEffect(() => {
    const s = String(text ?? '');
    const m = s.match(/-?[\d,]+(\.\d+)?/);
    if (!m) { setT(s); return undefined; }
    const num = parseFloat(m[0].replace(/,/g, ''));
    if (!isFinite(num) || num === 0) { setT(s); return undefined; }
    const dec = m[1] ? m[1].length - 1 : 0;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      const v = Number((num * e).toFixed(dec)).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
      setT(s.replace(m[0], v));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text, dur]);
  return <>{t}</>;
}

export function RestPageBrief({ isEs, summary, alert, kpi, testid = 'rest-page-brief' }) {
  const rachel = TEAM_AVATARS.find((a) => a.key === 'rachel');
  if (!summary && !kpi) return null;
  return (
    <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 shadow-sm" data-testid={testid}>
      <div className="flex items-start gap-3 flex-1 min-w-0">
        {rachel && <img src={rachel.img} alt="Rachel" className="w-10 h-10 rounded-full object-cover shrink-0" />}
        <div className="min-w-0">
          <p className="text-[10.5px] text-gray-400 font-bold flex items-center gap-1">Rachel · {isEs ? 'Resumen' : 'Summary'} <AgentHelp agent="rachel" isEs={isEs} /></p>
          <p className="mt-0.5 text-[13.5px] text-gray-800 leading-snug">{summary}</p>
          {alert && (
            <p className="mt-2 inline-flex items-start gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-[11.5px] font-semibold text-amber-800 leading-snug" data-testid={`${testid}-alert`}>
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-[1px]" aria-hidden="true" /> {alert}
            </p>
          )}
        </div>
      </div>
      {kpi && (
        <div className="shrink-0 sm:text-right sm:pl-5 sm:border-l border-gray-100" data-testid={`${testid}-kpi`}>
          <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-bold">{kpi.label}</p>
          <p className="text-[28px] sm:text-[30px] font-bold tracking-tight tabular-nums text-gray-900 leading-none mt-1"><CountUp text={kpi.value} /></p>
          {kpi.sub && <p className={`text-[11.5px] font-semibold mt-1 ${kpi.down ? 'text-red-500' : 'text-emerald-600'}`}>{kpi.sub}</p>}
        </div>
      )}
    </div>
  );
}

export function RHead({ title, sub, right }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
      <div>
        <h1 className="text-[30px] lg:text-[34px] font-bold tracking-tight text-gray-900" data-testid="rest-page-title">{title}</h1>
        {sub && <p className="mt-1 text-[14px] text-gray-500">{sub}</p>}
      </div>
      {right}
    </div>
  );
}

/* Official platform branding — logos served from /public/brands. */
export const BRAND = {
  toast: { label: 'Toast POS', color: '#FF4C00', icon: '/brands/toast.svg' },
  doordash: { label: 'DoorDash', color: '#FF3008', icon: '/brands/doordash.svg' },
  ubereats: { label: 'Uber Eats', color: '#06C167', icon: '/brands/ubereats.svg' },
  grubhub: { label: 'Grubhub', color: '#FF8000', icon: '/brands/grubhub.svg' },
  direct: { label: 'Direct / Website', color: '#1F2937', icon: '/brands/direct.svg' },
  google: { label: 'Google', color: '#4285F4', icon: '/brands/google.svg' },
  yelp: { label: 'Yelp', color: '#FF1A1A', icon: '/brands/yelp.svg' },
  tripadvisor: { label: 'Tripadvisor', color: '#34E0A1', icon: '/brands/tripadvisor.svg' },
};

export function BrandMark({ k, size = 16, className = '' }) {
  const b = BRAND[k];
  if (!b) return <span className={`inline-block rounded-full bg-gray-300 shrink-0 ${className}`} style={{ width: size, height: size }} />;
  return (
    <span className={`inline-flex items-center justify-center rounded-lg bg-white border border-gray-200 shrink-0 ${className}`}
      style={{ width: size + 12, height: size + 12 }} title={b.label}>
      <img src={b.icon} alt={b.label} style={{ width: size, height: size }} />
    </span>
  );
}

/* Honest module page for not-yet-connected intelligence modules. Zero fake data. */
/* Client/demo logo badge: the disc adopts the LOGO's own background color (sampled
   from its corners) so the badge never clashes with the card behind it. White fallback. */
export function LogoBadge({ src, alt = '', className = '', testid }) {
  const [bg, setBg] = React.useState('#ffffff');
  const onLoad = (e) => {
    try {
      const img = e.target;
      const c = document.createElement('canvas');
      c.width = c.height = 10;
      const ctx = c.getContext('2d');
      ctx.drawImage(img, 0, 0, 10, 10);
      const corners = [[0, 0], [9, 0], [0, 9], [9, 9]].map(([x, y]) => ctx.getImageData(x, y, 1, 1).data);
      const solid = corners.filter((d) => d[3] > 200);
      if (solid.length >= 3) {
        const avg = (i) => Math.round(solid.reduce((s, d) => s + d[i], 0) / solid.length);
        setBg(`rgb(${avg(0)},${avg(1)},${avg(2)})`);
      }
    } catch { /* tainted canvas (cross-origin) — keep white */ }
  };
  return (
    <img src={src} alt={alt} onLoad={onLoad} data-testid={testid}
      style={{ backgroundColor: bg }} className={`object-contain ${className}`} />
  );
}

export function ModuleNotConnected({ isEs, title, desc_en, desc_es, needs, testid }) {
  return (
    <div data-testid={testid}>
      <RHead title={title} sub={isEs ? desc_es : desc_en} />
      <RCard className="p-10 text-center">
        <p className="text-4xl mb-4">🔌</p>
        <SourceBadge source="not_connected" isEs={isEs} />
        <p className="mt-4 text-[15px] text-gray-600 max-w-lg mx-auto">
          {isEs
            ? 'Este módulo se activará cuando conectemos la fuente de datos. Sin datos reales, no mostramos nada — cero métricas falsas.'
            : 'This module activates once we connect its data source. Without real data we show nothing — zero fake metrics.'}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {needs.map((n) => (
            <span key={n} className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-[12px] text-gray-500">{n}</span>
          ))}
        </div>
        <a href="/restaurant/connections" data-testid="module-request-connection"
          className="mt-6 inline-block rounded-full bg-[var(--rest-accent)] hover:opacity-90 text-white font-bold text-[13px] px-6 py-2.5 transition-opacity">
          {isEs ? 'Solicitar conexión →' : 'Request connection →'}
        </a>
        <p className="mt-4 text-[12px] text-gray-400">
          {isEs ? 'Mientras tanto, puedes subir reportes en ' : 'Meanwhile, you can upload reports in '}
          <a href="/restaurant/reports" className="text-[var(--rest-accent)] font-semibold hover:underline">Reports</a>.
        </p>
      </RCard>
    </div>
  );
}
